/**
 * Типы для Auth module
 * Портировано из lib/features/auth/domain/entities/
 */

/** Учетные данные для входа */
export interface LoginRequest {
  email: string
  password: string
}

/** JWT токены */
export interface AuthTokens {
  accessToken: string
  refreshToken: string
}

/** Роли пользователя */
export type UserRole =
  | 'ceo'
  | 'admin'
  // Роль администратора админ-панели, заводится миграцией auth наравне с ADMIN.
  | 'admin_user'
  | 'hos'
  | 'manager'
  | 'support_manager'
  | 'agent'
  | 'user'
  | 'marketer'
  // DEV-1021. Менеджер каталога Объектов.
  | 'project_manager'
  // DEV-1167. Внешний интегратор партнёрского портала документации. В кабинете
  // агента ему делать нечего — см. PORTAL_ALLOWED_ROLES.
  | 'partner'

/** Пользователь */
export interface User {
  id: string
  email: string
  name: string
  first_name?: string
  last_name?: string
  status: number
  roles: UserRole[]
  created_at?: {
    seconds: number
    nanos: number
  }
  updated_at?: {
    seconds: number
    nanos: number
  }
  avatar?: string
}

/** Ответ на запрос логина */
export interface LoginResponse {
  tokens: AuthTokens
  user: User
}

/** Ответ на запрос обновления токена */
/**
 * POST /api/v1/auth/impersonation/exchange (DEV-2400): токен вкладки просмотра.
 * Поля — snake_case, как отдаёт gateway auth.
 */
export interface ImpersonationTokenResponse {
  access_token: string
  expires_at: string
  impersonator?: {
    user_id?: string
    full_name?: string
    session_id?: string
    expires_at?: string
  }
}

export interface RefreshTokenResponse {
  tokens: AuthTokens
}

/** Настройки пользователя (из auth-service) */
export interface UserSettings {
  user_id: string
  timezone: string
  avatar_url?: string
  telegram_chat_id?: number
  telegram_username?: string
  telegram_bot_verified: boolean
  whatsapp_phone?: string
  languages?: string[]
  created_at?: { seconds: number; nanos: number }
  updated_at?: { seconds: number; nanos: number }
}

/** Параметры обновления настроек пользователя */
export interface UpdateUserSettingsParams {
  timezone?: string
  avatar_url?: string
  whatsapp_phone?: string
  languages?: string[]
  clear_languages?: boolean
}

/** Состояние аутентификации */
export type AuthState = 'unauthenticated' | 'loading' | 'authenticated' | 'error'

/** Маппинг ролей на человекочитаемый текст */
export const ROLE_LABELS: Record<UserRole, string> = {
  // DEV-464. CEO — генеральный директор. Реальные права у него от парной роли admin,
  // эта роль-метка нужна для отображения и попадания в список менеджеров воронки.
  ceo: 'Генеральный директор',
  admin: 'Администратор',
  admin_user: 'Администратор админ-панели',
  hos: 'Руководитель отдела продаж',
  manager: 'Менеджер',
  // DEV-699. Сопровождающий менеджер: видит только сопровождаемые сделки,
  // не создаёт/не отменяет/не удаляет сделки.
  support_manager: 'Сопровождающий менеджер',
  agent: 'Агент',
  user: 'Пользователь',
  marketer: 'Маркетолог',
  // DEV-1021. Менеджер каталога — управляет объектами и материалами ЖК.
  project_manager: 'Менеджер каталога',
  // DEV-1167. Внешний интегратор: только партнёрский портал документации.
  partner: 'Партнёр',
}

/**
 * Приоритет ролей для отображения: показываем самую старшую роль пользователя.
 * DEV-464. ceo выше admin: у гендиректора пара ролей admin+ceo, и в UI он должен
 * отображаться как «Генеральный директор», а не «Администратор».
 */
export const ROLE_PRIORITY: UserRole[] = [
  'ceo',
  'admin',
  'admin_user',
  'hos',
  'marketer',
  'manager',
  'support_manager',
  // DEV-1021. Менеджер каталога — узкая роль, ниже менеджеров, выше агента.
  'project_manager',
  'agent',
  // DEV-1167. Партнёр — внешний контрагент, ниже любой внутренней роли.
  'partner',
  'user',
]

/**
 * Роли, которым открыт вход в кабинет агента (DEV-1167, DEV-1177).
 *
 * Это allowlist, а не запрет партнёра: роли заводятся в auth и приезжают сюда
 * готовым списком, поэтому следующая внешняя роль при запрете-по-имени снова
 * оказалась бы внутри по умолчанию. Здесь наоборот — незнакомая роль внутрь не
 * попадает, пока её сюда явно не добавят.
 *
 * Состав повторяет то, что пускалось до появления гварда, минус partner:
 * сужать доступ сотрудникам эта задача не просит.
 */
export const PORTAL_ALLOWED_ROLES: readonly UserRole[] = [
  'ceo',
  'admin',
  'admin_user',
  'hos',
  'manager',
  'support_manager',
  'marketer',
  'project_manager',
  'agent',
  'user',
]

/**
 * isRoleAllowedInPortal сообщает, пускать ли пользователя с такими ролями в
 * кабинет агента.
 *
 * Ключи ролей приходят с бэкенда в верхнем регистре и иногда с префиксом
 * `role:` — сравнение регистронезависимое и с отбрасыванием префикса. На этом
 * уже обжигались в DEV-1114, где роль-гейт из-за регистра не срабатывал никогда.
 *
 * Пользователь без ролей вовсе — пускается: так вело себя приложение до гварда.
 */
export function isRoleAllowedInPortal(roles: string[] | undefined): boolean {
  if (!roles || roles.length === 0) return true

  return roles.some((role) =>
    PORTAL_ALLOWED_ROLES.includes(role.replace(/^role:/i, '').toLowerCase() as UserRole),
  )
}

/**
 * getPrimaryRole возвращает приоритетную роль пользователя в нижнем регистре.
 * У пользователя может быть несколько ролей, а ключи с бэкенда приходят в
 * верхнем регистре — поэтому нормализуем регистр и выбираем старшую роль, а не
 * первую попавшуюся (DEV-196).
 */
export function getPrimaryRole(roles: string[] | undefined): UserRole | '' {
  if (!roles || roles.length === 0) return ''

  const normalized = roles.map((role) => role.toLowerCase())

  for (const role of ROLE_PRIORITY) {
    if (normalized.includes(role)) return role
  }

  // Роль есть, но вне известного списка — возвращаем первую как есть.
  return (normalized[0] as UserRole) || ''
}
